import type React from "react";
import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button, useToast, ToastType, Spinner } from "@cynaps/ui";
import "./PayoutPage.css";

interface BankDetails {
  bank_name: string;
  account_number: string;
  ifsc_code: string;
  account_holder_name: string;
  upi_id: string;
  has_bank_details: boolean;
  has_upi: boolean;
}

interface PayoutHistory {
  id: number;
  amount: number;
  status: string;
  payout_method: string;
  transaction_id: string;
  failure_reason: string;
  requested_at: string;
  processed_at: string | null;
}

interface EarningsSummary {
  available_balance: number;
  pending_approval?: number;
  pending_payout?: number;
}

interface PayoutPageProps {
  /** Type of user: 'annotator' or 'expert' */
  userType: "annotator" | "expert";
  /** Route to redirect on back button */
  backRoute: string;
  /** Route to redirect on auth failure */
  loginRoute: string;
}

const MINIMUM_PAYOUT = 1; // TODO: Change back to 100 later

const GoldCoinIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    style={{
      filter: "drop-shadow(0 2px 4px rgba(234, 179, 8, 0.25))",
      verticalAlign: "middle",
      marginLeft: "4px",
      display: "inline-block",
      flexShrink: 0,
    }}
  >
    <defs>
      <linearGradient id="payoutCoinGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#EAB308" />
        <stop offset="100%" stopColor="#A16207" />
      </linearGradient>
      <linearGradient id="payoutCoinInner" x1="1" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="100%" stopColor="#CA8A04" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="url(#payoutCoinGrad)" />
    <circle cx="16" cy="16" r="10.5" fill="url(#payoutCoinInner)" />
    <path
      d="M19 11.5 A 5.5 5.5 0 1 0 19 20.5"
      stroke="#FEF9C3"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <line
      x1="16"
      y1="9"
      x2="16"
      y2="23"
      stroke="#FEF9C3"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const CreditsDisplay: React.FC<{ amount: number; size?: number; className?: string }> = ({
  amount,
  size = 20,
  className = "",
}) => (
  <span className={`credits-display-inline ${className}`} style={{ display: "inline-flex", alignItems: "center" }}>
    {Math.round(amount)}
    <GoldCoinIcon size={size} />
  </span>
);

const BankIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v4M12 14v4M16 14v4" />
  </svg>
);

const UpiIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const ArrowLeftIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const CheckIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Shared Payout component for both Annotators and Experts
 */
export const PayoutPage: React.FC<PayoutPageProps> = ({ userType, backRoute, loginRoute }) => {
  const history = useHistory();
  const toast = useToast();

  // API endpoints based on user type
  const apiBase = userType === "expert" ? "/api/annotators/expert" : "/api/annotators";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
  const [payoutHistory, setPayoutHistory] = useState<PayoutHistory[]>([]);
  const [earnings, setEarnings] = useState<EarningsSummary | null>(null);

  const [amount, setAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState<"bank_transfer" | "upi">("bank_transfer");
  const [showBankForm, setShowBankForm] = useState(false);

  const [bankForm, setBankForm] = useState({
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    account_holder_name: "",
    upi_id: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [bankRes, payoutRes, earningsRes] = await Promise.all([
        fetch(`${apiBase}/bank-details`, { credentials: "include" }),
        fetch(`${apiBase}/payouts`, { credentials: "include" }),
        fetch(`${apiBase}/earnings`, { credentials: "include" }),
      ]);

      if (bankRes.status === 403 || bankRes.status === 401) {
        history.push(loginRoute);
        return;
      }

      const bankData = await bankRes.json();
      const payoutData = await payoutRes.json();
      const earningsData = await earningsRes.json();

      setBankDetails(bankData);
      setPayoutHistory(payoutData.payouts || []);
      setEarnings(earningsData);

      // Pre-fill bank form
      setBankForm({
        bank_name: bankData.bank_name || "",
        account_number: "",
        ifsc_code: bankData.ifsc_code || "",
        account_holder_name: bankData.account_holder_name || "",
        upi_id: bankData.upi_id || "",
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast?.show({
        message: "Failed to load data",
        type: ToastType.error,
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [apiBase, history, loginRoute, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleBankFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBankForm({ ...bankForm, [name]: value });
  };

  const saveBankDetails = async () => {
    try {
      const response = await fetch(`${apiBase}/bank-details`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bankForm),
      });

      if (response.ok) {
        toast?.show({
          message: "Bank details saved successfully",
          type: ToastType.info,
          duration: 3000,
        });
        setShowBankForm(false);
        fetchData();
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast?.show({
        message: "Failed to save bank details",
        type: ToastType.error,
        duration: 3000,
      });
    }
  };

  const submitPayoutRequest = async () => {
    const numAmount = Number.parseFloat(amount);

    if (isNaN(numAmount) || numAmount < MINIMUM_PAYOUT) {
      toast?.show({
        message: `Minimum payout amount is ${MINIMUM_PAYOUT} credits`,
        type: ToastType.error,
        duration: 3000,
      });
      return;
    }

    if (earnings && numAmount > earnings.available_balance) {
      toast?.show({
        message: "Insufficient balance",
        type: ToastType.error,
        duration: 3000,
      });
      return;
    }

    if (payoutMethod === "bank_transfer" && !bankDetails?.has_bank_details) {
      toast?.show({
        message: "Please add bank details first",
        type: ToastType.error,
        duration: 3000,
      });
      return;
    }

    if (payoutMethod === "upi" && !bankDetails?.has_upi) {
      toast?.show({
        message: "Please add UPI ID first",
        type: ToastType.error,
        duration: 3000,
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${apiBase}/payouts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: numAmount,
          payout_method: payoutMethod,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast?.show({
          message: "Payout request submitted successfully!",
          type: ToastType.info,
          duration: 3000,
        });
        setAmount("");
        fetchData();
      } else {
        toast?.show({
          message: data.errors?.[0] || "Failed to submit request",
          type: ToastType.error,
          duration: 4000,
        });
      }
    } catch (error) {
      toast?.show({
        message: "Failed to submit payout request",
        type: ToastType.error,
        duration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="payout-loading">
        <Spinner size={36} />
        <p>Loading payout details...</p>
      </div>
    );
  }

  const hasPendingPayout = payoutHistory.some((p) => p.status === "pending" || p.status === "processing");
  const pendingAmount = earnings?.pending_approval ?? earnings?.pending_payout ?? 0;
  const availableBalance = earnings?.available_balance || 0;
  const isAmountExceeded = amount !== "" && Number.parseFloat(amount) > availableBalance;

  return (
    <div className="payout-page">
      <motion.div
        className="payout-container"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        {/* Header */}
        <header className="payout-header">
          <button className="back-btn" onClick={() => history.push(backRoute)}>
            <ArrowLeftIcon size={14} />
            <span>Back to Earnings</span>
          </button>
          <div className="header-title-row">
            <h1 className="payout-title">Request Payout</h1>
          </div>
        </header>

        {/* Balance Info Cards */}
        <div className="balance-info-grid">
          <motion.div
            className="balance-card balance-card-available"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <span className="balance-card-label">Available Balance</span>
            <div className="balance-card-value">
              <CreditsDisplay amount={availableBalance} size={28} />
            </div>
            <span className="balance-card-sub">Ready to withdraw</span>
          </motion.div>

          <motion.div
            className="balance-card balance-card-pending"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <span className="balance-card-label">Pending</span>
            <div className="balance-card-value pending-val">
              <CreditsDisplay amount={pendingAmount} size={28} />
            </div>
            <span className="balance-card-sub">Awaiting verification</span>
          </motion.div>

          <motion.div
            className="balance-card balance-card-min"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <span className="balance-card-label">Minimum Payout</span>
            <div className="balance-card-value">
              <CreditsDisplay amount={MINIMUM_PAYOUT} size={28} />
            </div>
            <span className="balance-card-sub">Per transaction</span>
          </motion.div>
        </div>

        {/* Payout Request Form */}
        <div className="payout-card payout-form-section">
          <div className="card-header">
            <h2 className="card-title">New Payout Request</h2>
          </div>

          {hasPendingPayout ? (
            <div className="pending-notice">
              <span className="pending-dot" />
              <span>You have a pending payout request in queue. Please wait for it to be processed before submitting another.</span>
            </div>
          ) : (
            <div className="payout-form-body">
              <div className="form-group">
                <div className="form-label-row">
                  <label htmlFor="payout-amount-input">Amount (Credits)</label>
                  {availableBalance > 0 && (
                    <button
                      type="button"
                      className="quick-max-btn"
                      onClick={() => setAmount(availableBalance.toString())}
                    >
                      MAX ({availableBalance})
                    </button>
                  )}
                </div>
                <div className="input-with-icon-wrapper">
                  <input
                    id="payout-amount-input"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Min: ${MINIMUM_PAYOUT}`}
                    min={MINIMUM_PAYOUT}
                    max={availableBalance}
                    className={`payout-input ${isAmountExceeded ? "input-error" : ""}`}
                  />
                  <div className="input-coin-accessory">
                    <GoldCoinIcon size={20} />
                  </div>
                </div>
                {isAmountExceeded && (
                  <span className="error-text">Amount exceeds your available balance ({availableBalance} Credits)</span>
                )}
              </div>

              <div className="form-group">
                <label>Payout Method</label>
                <div className="method-grid">
                  <div
                    className={`method-card ${payoutMethod === "bank_transfer" ? "is-selected" : ""}`}
                    onClick={() => setPayoutMethod("bank_transfer")}
                  >
                    <div className="method-card-left">
                      <div className="method-icon-circle">
                        <BankIcon size={18} />
                      </div>
                      <div className="method-info">
                        <span className="method-name">Bank Transfer</span>
                        <span className="method-desc">Direct to bank account</span>
                      </div>
                    </div>
                    {bankDetails?.has_bank_details ? (
                      <span className="method-badge badge-configured">
                        <CheckIcon size={12} /> Configured
                      </span>
                    ) : (
                      <span className="method-badge badge-missing">Setup needed</span>
                    )}
                  </div>

                  <div
                    className={`method-card ${payoutMethod === "upi" ? "is-selected" : ""}`}
                    onClick={() => setPayoutMethod("upi")}
                  >
                    <div className="method-card-left">
                      <div className="method-icon-circle">
                        <UpiIcon size={18} />
                      </div>
                      <div className="method-info">
                        <span className="method-name">UPI</span>
                        <span className="method-desc">Instant VPA transfer</span>
                      </div>
                    </div>
                    {bankDetails?.has_upi ? (
                      <span className="method-badge badge-configured">
                        <CheckIcon size={12} /> Configured
                      </span>
                    ) : (
                      <span className="method-badge badge-missing">Setup needed</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bank / UPI Details Summary */}
              <div className="account-details-box">
                {payoutMethod === "bank_transfer" && (
                  bankDetails?.has_bank_details ? (
                    <div className="configured-details">
                      <div className="details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Bank</span>
                          <span className="detail-val">{bankDetails.bank_name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Account No.</span>
                          <span className="detail-val font-mono">{bankDetails.account_number}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">IFSC</span>
                          <span className="detail-val font-mono">{bankDetails.ifsc_code}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Account Holder</span>
                          <span className="detail-val">{bankDetails.account_holder_name || "—"}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="edit-details-btn"
                        onClick={() => setShowBankForm(true)}
                      >
                        Edit Details
                      </button>
                    </div>
                  ) : (
                    <div className="missing-details-state">
                      <p className="missing-text">No bank details added yet</p>
                      <button
                        type="button"
                        className="setup-action-btn"
                        onClick={() => setShowBankForm(true)}
                      >
                        + Add Bank Details
                      </button>
                    </div>
                  )
                )}

                {payoutMethod === "upi" && (
                  bankDetails?.has_upi ? (
                    <div className="configured-details">
                      <div className="details-grid single-col">
                        <div className="detail-item">
                          <span className="detail-label">UPI ID</span>
                          <span className="detail-val font-mono">{bankDetails.upi_id}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="edit-details-btn"
                        onClick={() => setShowBankForm(true)}
                      >
                        Edit UPI ID
                      </button>
                    </div>
                  ) : (
                    <div className="missing-details-state">
                      <p className="missing-text">No UPI ID added yet</p>
                      <button
                        type="button"
                        className="setup-action-btn"
                        onClick={() => setShowBankForm(true)}
                      >
                        + Add UPI ID
                      </button>
                    </div>
                  )
                )}
              </div>

              <button
                type="button"
                className="submit-payout-btn"
                onClick={submitPayoutRequest}
                disabled={submitting || !amount || Number.parseFloat(amount) < MINIMUM_PAYOUT || isAmountExceeded}
              >
                {submitting ? (
                  <span className="btn-loading-content">
                    <Spinner size={16} /> Submitting Request...
                  </span>
                ) : (
                  <span>Submit Payout Request</span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Payout History */}
        <div className="payout-card payout-history-section">
          <div className="card-header">
            <h2 className="card-title">Payout History</h2>
          </div>

          {payoutHistory.length === 0 ? (
            <div className="empty-history-state">
              <span className="empty-history-title">No payout requests yet</span>
              <span className="empty-history-sub">Once you request a withdrawal, your transaction history will appear here.</span>
            </div>
          ) : (
            <div className="history-list">
              {payoutHistory.map((payout) => {
                const statusClass = payout.status?.toLowerCase() || "default";
                return (
                  <div key={payout.id} className="history-card">
                    <div className="history-main-left">
                      <div className="history-method-icon">
                        {payout.payout_method === "bank_transfer" ? <BankIcon size={16} /> : <UpiIcon size={16} />}
                      </div>
                      <div className="history-meta-details">
                        <div className="history-title-row">
                          <span className="history-method-label">
                            {payout.payout_method === "bank_transfer" ? "Bank Transfer" : "UPI Transfer"}
                          </span>
                          <span className="history-date">{formatDate(payout.requested_at)}</span>
                        </div>
                        {payout.transaction_id && (
                          <span className="history-tx-id">Ref: {payout.transaction_id}</span>
                        )}
                        {payout.failure_reason && (
                          <span className="history-failure">{payout.failure_reason}</span>
                        )}
                      </div>
                    </div>

                    <div className="history-right-col">
                      <div className="history-amount-pill">
                        <CreditsDisplay amount={payout.amount} size={18} />
                      </div>
                      <span className={`payout-status-pill status-${statusClass}`}>
                        <span className="status-dot" />
                        {payout.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Bank / UPI Details Modal */}
      <AnimatePresence>
        {showBankForm && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowBankForm(false)}
          >
            <motion.div
              className="modal-container"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <header className="modal-header">
                <h3 className="modal-title">Payment Account Details</h3>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setShowBankForm(false)}
                  aria-label="Close"
                >
                  <CloseIcon size={18} />
                </button>
              </header>

              <div className="modal-body">
                <div className="modal-section-group">
                  <h4 className="modal-section-title">Bank Account Details</h4>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      name="bank_name"
                      value={bankForm.bank_name}
                      onChange={handleBankFormChange}
                      placeholder="e.g. HDFC Bank, State Bank of India"
                      className="payout-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input
                      type="text"
                      name="account_number"
                      value={bankForm.account_number}
                      onChange={handleBankFormChange}
                      placeholder="Enter full account number"
                      className="payout-input font-mono"
                    />
                  </div>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>IFSC Code</label>
                      <input
                        type="text"
                        name="ifsc_code"
                        value={bankForm.ifsc_code}
                        onChange={handleBankFormChange}
                        placeholder="e.g. HDFC0001234"
                        className="payout-input font-mono"
                      />
                    </div>
                    <div className="form-group">
                      <label>Account Holder Name</label>
                      <input
                        type="text"
                        name="account_holder_name"
                        value={bankForm.account_holder_name}
                        onChange={handleBankFormChange}
                        placeholder="Name on account"
                        className="payout-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-section-group">
                  <h4 className="modal-section-title">UPI Details</h4>
                  <div className="form-group">
                    <label>UPI ID (VPA)</label>
                    <input
                      type="text"
                      name="upi_id"
                      value={bankForm.upi_id}
                      onChange={handleBankFormChange}
                      placeholder="e.g. name@okhdfcbank"
                      className="payout-input font-mono"
                    />
                  </div>
                </div>
              </div>

              <footer className="modal-footer">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => setShowBankForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="modal-save-btn"
                  onClick={saveBankDetails}
                >
                  Save Details
                </button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PayoutPage;
